import { useOptionalWorkspaceTabsActions } from 'features/workspaceTabs/contexts/WorkspaceTabsContext'
import { useEffect, useRef } from 'react'

/**
 * Registers a leave/close guard for the mounted route's dirty state. Covers
 * workspace tab activation/close/open and browser unload in one registration;
 * legacy-layout consumers get the beforeunload half only. Route-local
 * navigation (list row selects, section switches) should consult `isDirty`
 * directly at the interaction site.
 */
export function useWorkspaceLeaveGuard({
  message,
  isDirty,
}: {
  message: string
  isDirty: () => boolean
}): void {
  const actions = useOptionalWorkspaceTabsActions()
  const guardRef = useRef({ message, isDirty })
  guardRef.current = { message, isDirty }

  const registerNavigationGuard = actions?.registerNavigationGuard
  useEffect(() => {
    if (!registerNavigationGuard) return
    return registerNavigationGuard({
      message: guardRef.current.message,
      isDirty: () => guardRef.current.isDirty(),
    })
  }, [registerNavigationGuard])

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!guardRef.current.isDirty()) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])
}
